import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  DollarSign,
  TrendingUp,
  Target,
  BookOpen,
} from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import Layout from "../components/Layout";
import BottomNavigation from "../components/BottomNavigation";

interface Tip {
  id: string;
  title: string;
  content: string;
  category: string;
  icon: string;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
}

const Education: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const navigate = useNavigate();

  const tips: Tip[] = [
    {
      id: "1",
      title: "50-30-20 Rule",
      content:
        "Divide your income into: 50% for needs (housing, food), 30% for wants (leisure, hobbies) and 20% for savings and investments. This is a solid foundation for organizing your finances.",
      category: "Budget",
      icon: "BarChart3",
      difficulty: "Beginner",
    },
    {
      id: "2",
      title: "Emergency Reserve",
      content:
        "Always maintain a reserve equivalent to 3-6 months of your essential expenses. This reserve should be in an easily accessible account, such as savings or CDB.",
      category: "Savings",
      icon: "DollarSign",
      difficulty: "Beginner",
    },
    {
      id: "3",
      title: "Compound Interest",
      content:
        'Compound interest is "interest on interest". The longer you leave your money invested, the greater the growth. Start investing early, even with small amounts.',
      category: "Investments",
      icon: "TrendingUp",
      difficulty: "Intermediate",
    },
    {
      id: "4",
      title: "Diversification",
      content:
        "Never put all your eggs in one basket. Diversify your investments among different types of assets: fixed income, stocks, funds, etc.",
      category: "Investments",
      icon: "Target",
      difficulty: "Intermediate",
    },
    {
      id: "5",
      title: "Expense Control",
      content:
        "Write down all your expenses for at least one month. You'll be surprised how much you spend on small things. Use apps or spreadsheets to make control easier.",
      category: "Control",
      icon: "📝",
      difficulty: "Beginner",
    },
    {
      id: "6",
      title: "Avoid Unnecessary Debts",
      content:
        "Credit card is not extra money. If you can't pay in cash, avoid buying. Use credit only for emergencies or when you're sure you can pay.",
      category: "Debts",
      icon: "🚫",
      difficulty: "Beginner",
    },
    {
      id: "7",
      title: "Financial Goals",
      content:
        'Set clear and specific goals: "I want to save $10,000 in 12 months for a trip". Vague goals are hard to achieve.',
      category: "Planning",
      icon: "Target",
      difficulty: "Beginner",
    },
    {
      id: "8",
      title: "Continuous Financial Education",
      content:
        "Invest in your financial knowledge. Read books, follow specialized channels and always seek to learn about new investment opportunities.",
      category: "Education",
      icon: "BookOpen",
      difficulty: "Intermediate",
    },
    {
      id: "9",
      title: "Inflação e Poder de Compra",
      content:
        "A inflação reduz o poder de compra do seu dinheiro ao longo do tempo. Por isso, é importante investir em ativos que superem a inflação, como ações e fundos imobiliários.",
      category: "Investments",
      icon: "BarChart3",
      difficulty: "Advanced",
    },
    {
      id: "10",
      title: "Seguro e Proteção",
      content:
        "Proteja-se contra imprevistos com seguros adequados: vida, saúde, residencial. O custo do seguro é menor que o prejuízo de um evento inesperado.",
      category: "Proteção",
      icon: "🛡️",
      difficulty: "Intermediate",
    },
  ];

  const categories = [
    "all",
    "Budget",
    "Savings",
    "Investments",
    "Control",
    "Debts",
    "Planning",
    "Education",
    "Protection",
  ];

  const filteredTips =
    selectedCategory === "all"
      ? tips
      : tips.filter((tip) => tip.category === selectedCategory);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "Intermediate":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "Advanced":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <Layout
      title="Financial Education"
      subtitle="Learn fundamental concepts to make smarter financial decisions"
    >
      {/* Introdução */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-8"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
        </div>
      </motion.div>

      {/* Filtros por categoria */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-6"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Categories</h3>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === category
                  ? "bg-primary-600 text-white"
                  : "bg-dark-700 text-gray-300 hover:bg-dark-600"
              }`}
            >
              {category === "all" ? "Todas" : category}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Dicas financeiras */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="space-y-6"
      >
        <h3 className="text-lg font-semibold text-white">
          {selectedCategory === "all"
            ? "Todas as Dicas"
            : `Dicas de ${selectedCategory}`}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredTips.map((tip, index) => (
            <motion.div
              key={tip.id}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="card hover:bg-dark-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                    {tip.icon === "BarChart3" && (
                      <BarChart3 className="w-6 h-6 text-white" />
                    )}
                    {tip.icon === "DollarSign" && (
                      <DollarSign className="w-6 h-6 text-white" />
                    )}
                    {tip.icon === "TrendingUp" && (
                      <TrendingUp className="w-6 h-6 text-white" />
                    )}
                    {tip.icon === "Target" && (
                      <Target className="w-6 h-6 text-white" />
                    )}
                    {tip.icon === "BookOpen" && (
                      <BookOpen className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">
                      {tip.title}
                    </h4>
                    <p className="text-sm text-gray-400">{tip.category}</p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium border ${getDifficultyColor(
                    tip.difficulty
                  )}`}
                >
                  {tip.difficulty}
                </span>
              </div>

              <p className="text-gray-300 leading-relaxed">{tip.content}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Seção de recursos adicionais */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-12"
      >
        <h3 className="text-lg font-semibold text-white mb-6">
          Additional Resources
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="card text-center hover:bg-dark-700 transition-colors cursor-pointer"
          >
            <div className="text-4xl mb-4">📖</div>
            <h4 className="text-lg font-semibold text-white mb-2">
              Recommended Books
            </h4>
            <p className="text-gray-400 text-sm">
              "Pai Rico, Pai Pobre", "Os Segredos da Mente Milionária",
              "Investimentos Inteligentes"
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="card text-center hover:bg-dark-700 transition-colors cursor-pointer"
          >
            <div className="text-4xl mb-4">🎥</div>
            <h4 className="text-lg font-semibold text-white mb-2">
              Canais YouTube
            </h4>
            <p className="text-gray-400 text-sm">
              "Me Poupe!", "Primo Rico", "O Primo Rico", "Nathalia Arcuri"
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="card text-center hover:bg-dark-700 transition-colors cursor-pointer"
          >
            <div className="text-4xl mb-4">📱</div>
            <h4 className="text-lg font-semibold text-white mb-2">
              Apps Úteis
            </h4>
            <p className="text-gray-400 text-sm">
              "GuiaBolso", "Organizze", "Mobills", "Minhas Economias"
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Dica do dia */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="mt-12"
      >
        <div className="card bg-gradient-to-r from-primary-600/20 to-blue-600/20 border-primary-500/30">
          <div className="flex items-center space-x-4">
            <div className="text-4xl">💡</div>
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">
                Dica do Dia
              </h4>
              <p className="text-gray-300">
                "O dinheiro que você não vê, você não gasta. Configure
                transferências automáticas para sua poupança assim que receber
                seu salário."
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <BottomNavigation />
    </Layout>
  );
};

export default Education;
